import { formatToAED, formatToINR, formatToUSD } from '@/helpers/formatToINR'
import {
  useAddToWishlist,
  useRemoveFromWishlist,
  useWishlist,
} from '@/hooks/useWishlist'
import { Product } from '@/types/api'
import { easeInOut, motion } from 'framer-motion'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useState, useCallback, memo, useRef, useEffect } from 'react'
import ScrollFade from './ScrollFade'
import Loading from './Loading'
import { useCurrency } from './CurrencyProvider'

interface ItemCardProps {
  data: Product
  isAddedToWishList?: boolean
  handleHeartClick?: any
  toastId: number | null
  theme?: string
}

const ItemCardDynamic: React.FC<ItemCardProps> = memo(
  ({ data, isAddedToWishList, handleHeartClick, toastId, theme }) => {
    const router = useRouter()
    const [isHovered, setIsHovered] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const { currency } = useCurrency()

    const handleMouseEnter = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      setIsHovered(true)
      if (videoRef.current) {
        videoRef.current.play().catch((error) => {
          console.log('Video play failed:', error)
        })
      }
    }, [])

    const handleMouseLeave = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      setIsHovered(false)
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0
      }
    }, [])

    const handleClick = useCallback(() => {
      router.push('/product-details/' + data.product_id)
    }, [router, data.product_id])

    const renderMedia = () => {
      return (
        <div
          className={`relative w-full lg:h-[400px] h-[300px] ${
            theme === 'white' && 'border border-solid border-[#A08260]'
          }`}
        >
          <div className='relative w-full h-full'>
            <img
              src={
                data.image_url_list && data.image_url_list?.length
                  ? data.image_url_list[0]?.url ?? ''
                  : ''
              }
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                isHovered ? 'opacity-0' : 'opacity-100'
              }`}
              alt={data.name}
              loading='lazy'
            />
          </div>
          {data.video_url_list && (
            <video
              ref={videoRef}
              //preload="none"
              className={`absolute inset-0 w-full h-full object-contain lg:object-cover transition-opacity duration-500 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              muted
              playsInline
              loop
              preload='auto'
              onContextMenu={(e) => e.preventDefault()}
            >
              <source src={data.video_url_list[0]?.url} type='video/mp4' />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      )
    }

    const getFormattedPrices = (product: any) => {
      if (!product) return { sp: '', mrp: '' }

      let sp = ''
      let mrp = ''

      if (currency === 'AED') {
        sp = formatToAED(+product?.dirhams_mrp)
        mrp = formatToAED(+product?.dirhams_mrp)
      } else if (currency === 'USD') {
        sp = formatToUSD(+product?.dollar_mrp)
        mrp = formatToUSD(+product?.dollar_mrp)
      } else {
        sp = formatToINR(+product?.sp)
        mrp = formatToINR(+product?.mrp)
      }

      return { sp, mrp }
    }
    const { sp, mrp } = getFormattedPrices(data)

    return (
      <>
        <div
          className={`flex group md:min-h-[527px] w-full min-w-[200px] bg-white duration-300 cursor-pointer`}
          onClick={handleClick}
        >
          <div className='w-full flex flex-col'>
            <div
              className='relative'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {renderMedia()}
              <div className='right-0 bottom-0 absolute m-4 cursor-pointer z-10'>
                <div className='relative'>
                  {toastId === data.product_id && (
                    <motion.div
                      initial={{ opacity: '0%' }}
                      animate={{ opacity: toastId != null ? '100%' : '0%' }}
                      transition={{ duration: 0.4, ease: easeInOut }}
                      className='absolute -top-10 -left-[75px]'
                    >
                      <div
                        className="font-barlowMedium z-30 text-black bg-white py-[5px] px-[10px] rounded-full text-[12px] tracking-[1.5px] relative
                    before:content-[''] before:absolute before:z-20 before:w-3 before:h-3 before:bg-white before:-bottom-[4px] transform before:rotate-45 before:right-[10px]"
                      >
                        WISHLISTED
                      </div>
                    </motion.div>
                  )}
                  <Image
                    height={400}
                    width={400}
                    alt='Heart icon'
                    src={
                      true
                        ? isAddedToWishList
                          ? '/ic_heart_fill_red_theme.png'
                          : '/ic_heart_outline_red_theme.svg'
                        : isAddedToWishList
                        ? '/ic_heart_filled.png'
                        : '/ic_heart.png'
                    }
                    className='object-contain h-[32px] w-[32px]'
                    onClick={(e) => handleHeartClick(data.product_id, e)}
                  />
                </div>
              </div>
            </div>

            <div
              className={`flex flex-col flex-grow px-2 ${
                theme === 'black' ? 'bg-black' : 'bg-white'
              }`}
            >
              <div className='flex flex-row h-[16px] justify-start space-x-[6px] mt-3'>
                <Image
                  height={100}
                  width={100}
                  alt='Product image'
                  src='/ic_jewelery_mauve.svg'
                  className='w-[16px] h-[16px] p-0'
                />
                <Image
                  height={100}
                  width={100}
                  alt='Product image'
                  src='/ic_jewelery_gold.svg'
                  className='w-[16px] h-[16px] p-0'
                />
                <Image
                  height={100}
                  width={100}
                  alt='Product image'
                  src='/ic_jewelery_silver.svg'
                  className='w-[16px] h-[16px] p-0'
                />
              </div>

              <div className='text-start'>
                <div
                  className={`text-[16px] ${
                    theme === 'black' ? 'text-[#D9D6C1]' : 'text-black'
                  } font-barlowNormal mt-1 uppercase line-clamp-1 tracking-[1px]`}
                >
                  {data?.name}
                </div>
                <p
                  className={`font-barlowMedium text-[12px] pt-[2px] line-clamp-2 ${
                    theme === 'black' ? 'text-[#E6DB96]' : 'text-black'
                  }`}
                >
                  {data?.description}
                </p>
              </div>
              <div
                className={`font-cambonLight ${
                  theme === 'black' ? 'text-white' : 'text-black'
                } text-[20px] font-cambonRegular mt-[6.5px] text-start`}
              >
                <span>{mrp}</span>
                {sp !== mrp && (
                  <span className='ml-2 line-through opacity-60'>{sp}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }
)

ItemCardDynamic.displayName = 'ItemCardDynamic'

const ProductCardEveryDay = ({
  productList,
  isLoading,
  theme,
  section,
  justifyStyle,
}: {
  productList: Product[]
  isLoading: boolean
  theme?: string
  section?: string
  justifyStyle?: 'center' | 'start'
}) => {
  const justifyClasses: Record<string, string> = {
    center: 'justify-center',
    start: 'justify-start',
  }
  const [optimisticWishlist, setOptimisticWishlist] = useState<number[]>([])
  const [toastId, setToastId] = useState<number | null>(null)
  const { data: wishListItems } = useWishlist()
  const { mutate: addToWishlist } = useAddToWishlist()
  const { mutate: removeFromWishlist } = useRemoveFromWishlist()

  // --- Drag to scroll logic ---
  const carouselRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDragging.current = true
    startX.current = e.pageX - (carouselRef.current?.offsetLeft || 0)
    scrollLeft.current = carouselRef.current?.scrollLeft || 0
    document.body.style.cursor = 'grabbing'
  }

  const handleMouseLeave = () => {
    isDragging.current = false
    document.body.style.cursor = ''
  }

  const handleMouseUp = () => {
    isDragging.current = false
    document.body.style.cursor = ''
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    e.preventDefault()
    const x = e.pageX - (carouselRef.current?.offsetLeft || 0)
    const walk = (x - startX.current) * 1.2 // scroll-fast factor
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = scrollLeft.current - walk
    }
  }
  // --- End drag to scroll logic ---

  useEffect(() => {
    if (wishListItems?.data) {
      setOptimisticWishlist(wishListItems.data.map((item) => item.product_id))
    }
  }, [wishListItems])

  const handleHeartClick = (
    id: number,
    event: React.MouseEvent<HTMLImageElement>
  ) => {
    event.stopPropagation()
    const isCurrentlyWishlisted = optimisticWishlist.includes(id)
    const wishlistItem = wishListItems?.data.find((wl) => wl.product_id === id)

    if (isCurrentlyWishlisted) {
      setOptimisticWishlist((prev) => prev.filter((itemId) => itemId !== id))
    } else {
      setOptimisticWishlist((prev) => [...prev, id])
      setToastId(id)
      setTimeout(() => setToastId(null), 1500)
    }

    if (isCurrentlyWishlisted && wishlistItem) {
      removeFromWishlist(wishlistItem.wishlist_id, {
        onError: () => {
          setOptimisticWishlist((prev) => [...prev, id])
        },
      })
    } else {
      addToWishlist(
        {
          product_id: id,
        },
        {
          onError: () => {
            setOptimisticWishlist((prev) =>
              prev.filter((itemId) => itemId !== id)
            )
            setToastId(null)
          },
        }
      )
    }
  }

  return (
    <ScrollFade className="!opacity-100">
      <div
        className={`flex flex-nowrap  ${
          section === 'custom'
            ? 'overflow-x-auto md:overflow-hidden'
            : 'overflow-x-auto'
        } [&::-webkit-scrollbar]:hidden w-full ${
          justifyStyle ? justifyClasses[justifyStyle] : 'justify-start'
        } mb-0  px-0 ${
          theme === 'black' ? 'border-none bg-black' : 'border-none bg-white'
        }`}
        ref={carouselRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        style={{
          cursor: isDragging.current ? 'grabbing' : 'grab',
          userSelect: isDragging.current ? 'none' : 'auto',
          gap:"15px"
        }}
      >
        {isLoading ? (
          <>
            {/* <CircularProgressbar /> */}
            <div className='w-[100%]'>
              <div className='flex m-[10px] gap-4'>
                <Loading
                  classes={
                    'flex-grow flex relative max-h-screen sm:min-h-[435px] bg-[rgba(128,128,128,0.6)] !text-white'
                  }
                />
                <Loading
                  classes={
                    'flex-grow flex relative max-h-screen sm:min-h-[435px] bg-[rgba(128,128,128,0.6)] !text-white'
                  }
                />
                <Loading
                  classes={
                    'flex-grow flex relative max-h-screen sm:min-h-[435px] bg-[rgba(128,128,128,0.6)] !text-white'
                  }
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {productList?.map((item, index) => (
              <div
                key={item.product_id}
                className={`${
                  section === 'custom'
                    ? 'md:min-w-[20%] md:max-w-[20%]'
                    : 'min-w-[280px] max-w-[280px] lg:min-w-[300px] lg:max-w-[300px]'
                } `}
              >
                <ItemCardDynamic
                  key={item.product_id}
                  data={item}
                  isAddedToWishList={optimisticWishlist.includes(
                    item.product_id
                  )}
                  handleHeartClick={handleHeartClick}
                  toastId={toastId}
                  theme={theme}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </ScrollFade>
  )
}

ProductCardEveryDay.displayName = 'ProductCardEveryDay'

export default ProductCardEveryDay
